import { useRef, useState, useEffect } from "react";
import io from "socket.io-client";
import { useNavigate, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { handleLogout } from "../utils/Logout";
import { useAuthStorage } from "../hooks/useAuthStorage";

import PanelIcon from "../assets/icons/OpenPanel.svg";
import HandSelectLeftImage from "../assets/icons/HandSelectLeft.svg";
import HomeIcon from "../assets/icons/Home.svg";
import MessageIcon from "../assets/icons/Message.svg";
import BotIcon from "../assets/icons/Bot.svg";
import UploadIcon from "../assets/icons/Upload2.svg";
import OrdersIcon from "../assets/icons/Cart.svg";
import InventoryIcon from "../assets/icons/Package.svg";
import PricingIcon from "../assets/icons/Pricing.svg";
import DashboardIcon from "../assets/icons/DashboardGauge.svg";
import ChannelsIcon from "../assets/icons/Channels.svg";
import UsersIcon from "../assets/icons/Users.svg";
import CatalogIcon from "../assets/icons/Book.svg";
import SettingsIcon from "../assets/icons/Settings.svg";
import LightIcon from "../assets/icons/LightMode.svg";
import DarkIcon from "../assets/icons/DarkMode.svg";
import LogoutIcon from "../assets/icons/LogOut.svg";
import CalendarIcon from "../assets/icons/Calendar.svg";
import MoneyIcon from "../assets/icons/Money.svg";
import InsuranceIcon from "../assets/icons/life-buoy.svg";
import TrackerIcon from "../assets/icons/MapPin.svg";
import CampaignIcon from "../assets/icons/Campaign.svg";
import ReportsIcon from "../assets/icons/Report.svg";
import LucciIcon from "../assets/Logo.svg";
import SelectLanguage from "../components/SelectLanguage";
import useScreenSize from "../hooks/useScreenSize";

import useBusinessStore from "../state/businessStore";

import "./Sidebar.scss";

const Sidebar = () => {
  const { t } = useTranslation("sidebar");

  const { getToken } = useAuthStorage();

  const token = getToken();

  const [hasNewMessage, setHasNewMessage] = useState(false);

  const { width } = useScreenSize();

  const navigate = useNavigate();

  // State to handle collapsible sidebar
  const [isCollapsed, setIsCollapsed] = useState(false);

  // :: State management with Zustand
  const { getSelectedBusinessDetails, selectedBusiness, getUserRole, activeModules } = useBusinessStore();
  const { selectedBusinessName, selectedBusinessLogo } =
    getSelectedBusinessDetails();
  const isCollaborator = getUserRole() === 'Collaborator';

  const isModuleActive = (key) =>
    Array.isArray(activeModules) && activeModules.includes(key);

  const handleToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleSignOut = () => {
    handleLogout(navigate);
  };

  function acortarTexto(texto, limite = 15) {
    return texto.length > limite ? texto.slice(0, limite) + "..." : texto;
  }

  /****************************************************
   ****************** WebSockets **********************
   ****************************************************/
  const socketRef = useRef(null);

  //:: Establish socket connection
  const establishSocketConnection = (businessId) => {
    socketRef.current = io(`${import.meta.env.VITE_BASE_URL}/events`, {
      extraHeaders: { Authorization: `Bearer ${token}` },
    });

    // Event when the connection to the socket succeeds
    socketRef.current.on("connect", () => {});

    // Listen to incoming messages in real-time for the current business
    socketRef.current.on(businessId, (newMessage) => {
      if (newMessage) setHasNewMessage(true);
    });

    // Event when connection to the socket is disconnected
    socketRef.current.on("disconnect", () => {});
  };

  // useEffect(() => {
  // if (token && selectedBusiness) {
  // Establishes connection to the socket server for the current business
  // establishSocketConnection(selectedBusiness);
  // }

  // Cleanup: Disconnects the socket when disassembling the component or changing business
  // return () => {
  // if (socketRef.current) {
  //socketRef.current.disconnect();
  // }
  // };
  // }, [selectedBusiness]); // Execute only when the selected business changes

  return (
    <aside className={`sidebar ${isCollapsed ? "sidebar--collapsed" : ""}`}>
      <header className="sidebar__header">
        {!isCollapsed &&
          (selectedBusinessName && width < 800 ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <img
                src={selectedBusinessLogo}
                alt=""
                style={{ height: "30px", borderRadius: "8px" }}
              />
              <h1 className="sidebar__header-business">
                {acortarTexto(selectedBusinessName, 17)}
              </h1>
            </div>
          ) : (
            <h1 className="sidebar__header-business">LucciAI</h1>
          ))}
        {/* Expand / collapse button */}
        <button className="sidebar__header-toggle" onClick={handleToggle}>
          <img
            src={PanelIcon}
            className="sidebar__header-icon"
            alt="Panel icon"
          />
        </button>
      </header>

      {!selectedBusinessName ? (
        <div
          className="sidebar__navigation"
          style={{
            filter: isCollapsed ? "opacity(0)" : "opacity(100)",
            transitionDelay: !isCollapsed ? ".3s" : "0s",
          }}
        >
          <img src={HandSelectLeftImage} alt="" />
          <h2>{t("sidebar.select_business")}</h2>
          <p>{t("sidebar.select_business_description")}</p>
        </div>
      ) : (
        <nav className="sidebar__navigation">
          <ul className="sidebar__navigation-menu">
            {/* <NavLink
              to="/luccichat"
              className="sidebar__navigation-link"
              activeclassname="active"
            >
              <li className="sidebar__navigation-item  ia__chat">
                {!isCollapsed && (
                  <span className="sidebar__navigation-text">LucciChat</span>
                )}
                <img
                  src={LucciIcon}
                  className="sidebar__navigation-icon"
                  alt="Chat icon"
                />
              </li>
            </NavLink> */}
            {isModuleActive("home") && !isCollaborator && (
              <li className="sidebar__navigation-item">
                <NavLink
                  to="/"
                  end
                  className="sidebar__navigation-link"
                  activeclassname="active"
                >
                  <img
                    src={HomeIcon}
                    className="sidebar__navigation-icon"
                    alt="Home icon"
                  />
                  {!isCollapsed && (
                    <span className="sidebar__navigation-text">
                      {t("sidebar.home")}
                    </span>
                  )}
                </NavLink>
              </li>
            )}
            {isModuleActive("home") && (
              <li className="sidebar__navigation-item">
                <NavLink
                  to="/dashboard"
                  className="sidebar__navigation-link"
                  activeclassname="active"
                >
                  <img
                    src={DashboardIcon}
                    className="sidebar__navigation-icon"
                    alt="Dashboard icon"
                  />
                  {!isCollapsed && (
                    <span className="sidebar__navigation-text">
                      {t("sidebar.dashboard")}
                    </span>
                  )}
                </NavLink>
              </li>
            )}
            {isModuleActive("inbox") && !isCollaborator && (
              <li
                className="sidebar__navigation-item"
                onClick={() => setHasNewMessage(false)}
              >
                <NavLink
                  to="/inbox"
                  className="sidebar__navigation-link"
                  activeclassname="active"
                  style={{
                    position: "relative",
                  }}
                >
                  <img
                    src={MessageIcon}
                    className="sidebar__navigation-icon"
                    alt="Message icon"
                  />
                  {!isCollapsed && (
                    <span className="sidebar__navigation-text">
                      {t("sidebar.inbox")}
                    </span>
                  )}
                  {hasNewMessage && (
                    <div
                      style={{
                        width: "5px",
                        height: "5px",
                        background: "red",
                        borderRadius: "50%",
                        position: "absolute",
                        top: "50%",
                        right: "-13px",
                        transform: "translateY(-50%)",
                      }}
                    ></div>
                  )}
                </NavLink>
              </li>
            )}
            {isModuleActive("customers") && (
              <li className="sidebar__navigation-item">
                <NavLink
                  to="/customers"
                  className="sidebar__navigation-link"
                  activeclassname="active"
                >
                  <img
                    src={UsersIcon}
                    className="sidebar__navigation-icon"
                    alt="Customers icon"
                  />
                  {!isCollapsed && (
                    <span className="sidebar__navigation-text">
                      {t("sidebar.customers")}
                    </span>
                  )}
                </NavLink>
              </li>
            )}
            {isModuleActive("catalog") && (
              <li className="sidebar__navigation-item">
                <NavLink
                  to="/catalog"
                  className="sidebar__navigation-link"
                  activeclassname="active"
                >
                  <img
                    src={CatalogIcon}
                    className="sidebar__navigation-icon"
                    alt="Catalog icon"
                  />
                  {!isCollapsed && (
                    <span className="sidebar__navigation-text">
                      {t("sidebar.catalog")}
                    </span>
                  )}
                </NavLink>
              </li>
            )}
            {isModuleActive("lucci_ai") && !isCollaborator && (
              <li className="sidebar__navigation-item">
                <NavLink
                  to="/lucci"
                  className="sidebar__navigation-link"
                  activeclassname="active"
                >
                  <img
                    src={BotIcon}
                    className="sidebar__navigation-icon"
                    alt="Bot icon"
                  />
                  {!isCollapsed && (
                    <span className="sidebar__navigation-text">Lucci AI</span>
                  )}
                </NavLink>
              </li>
            )}
            {/*
          <li className="sidebar__navigation-item">
            <NavLink to="/orders" className="sidebar__navigation-link" activeclassnamee="active">
              <img src={OrdersIcon} className="sidebar__navigation-icon" alt="Orders icon" />
              {!isCollapsed && <span className="sidebar__navigation-text">{t('sidebar.orders')}</span>}
            </NavLink>
          </li>
          <li className="sidebar__navigation-item">
            <NavLink to="/pricing" className="sidebar__navigation-link" activeclassname="active">
              <img src={PricingIcon} className="sidebar__navigation-icon" alt="Pricing icon" />
              {!isCollapsed && <span className="sidebar__navigation-text">{t('sidebar.pricing')}</span>}
            </NavLink>
          </li> */}
            {isModuleActive("channels") && !isCollaborator && (
              <li className="sidebar__navigation-item">
                <NavLink
                  to="/channel"
                  className="sidebar__navigation-link"
                  activeclassname="active"
                >
                  <img
                    src={ChannelsIcon}
                    className="sidebar__navigation-icon"
                    alt="Channel admin icon"
                  />
                  {!isCollapsed && (
                    <span className="sidebar__navigation-text">
                      {t("sidebar.channels")}
                    </span>
                  )}
                </NavLink>
              </li>
            )}
            {isModuleActive("schedule") && (
              <li className="sidebar__navigation-item">
                <NavLink
                  to="/schedule"
                  className="sidebar__navigation-link"
                  activeclassname="active"
                >
                  <img
                    src={CalendarIcon}
                    className="sidebar__navigation-icon"
                    alt="Calendar icon"
                  />
                  {!isCollapsed && (
                    <span className="sidebar__navigation-text">
                      {t("sidebar.schedule")}
                    </span>
                  )}
                </NavLink>
              </li>
            )}
            {isModuleActive("users") && !isCollaborator && (
              <li className="sidebar__navigation-item">
                <NavLink
                  to="/user"
                  className="sidebar__navigation-link"
                  activeclassname="active"
                >
                  <img
                    src={UsersIcon}
                    className="sidebar__navigation-icon"
                    alt="Integrations icon"
                  />
                  {!isCollapsed && (
                    <span className="sidebar__navigation-text">
                      {t("sidebar.users")}
                    </span>
                  )}
                </NavLink>
              </li>
            )}
            {isModuleActive("bass_payments") && !isCollaborator && (
              <li className="sidebar__navigation-item">
                <NavLink
                  to="/payments"
                  className="sidebar__navigation-link"
                  activeclassname="active"
                >
                  <img
                    src={MoneyIcon}
                    className="sidebar__navigation-icon"
                    alt="Calendar icon"
                  />
                  {!isCollapsed && (
                    <span className="sidebar__navigation-text">
                      {t("sidebar.payments")}
                    </span>
                  )}
                </NavLink>
              </li>
            )}
            {isModuleActive("insurance") && (
              <li className="sidebar__navigation-item">
                <NavLink
                  to="/insurance"
                  className="sidebar__navigation-link"
                  activeclassname="active"
                >
                  <img
                    src={InsuranceIcon}
                    className="sidebar__navigation-icon"
                    alt="Insurance icon"
                  />
                  {!isCollapsed && (
                    <span className="sidebar__navigation-text">
                      {t("sidebar.insurance")}
                    </span>
                  )}
                </NavLink>
              </li>
            )}
            {isModuleActive("iot_trackers") && (
              <li className="sidebar__navigation-item">
                <NavLink
                  to="/iot-trackers"
                  className="sidebar__navigation-link"
                  activeclassname="active"
                >
                  <img
                    src={TrackerIcon}
                    className="sidebar__navigation-icon"
                    alt="IoT trackers icon"
                  />
                  {!isCollapsed && (
                    <span className="sidebar__navigation-text">
                      {t("sidebar.iot_trackers")}
                    </span>
                  )}
                </NavLink>
              </li>
            )}
            {isModuleActive("reports") && !isCollaborator && (
              <li className="sidebar__navigation-item">
                <NavLink
                  to="/reports"
                  className="sidebar__navigation-link"
                  activeclassname="active"
                >
                  <img
                    src={ReportsIcon}
                    className="sidebar__navigation-icon"
                    alt="Reports icon"
                  />
                  {!isCollapsed && (
                    <span className="sidebar__navigation-text">
                      {t("sidebar.reports")}
                    </span>
                  )}
                </NavLink>
              </li>
            )}
            {isModuleActive("campaigns") && !isCollaborator && (
              <li className="sidebar__navigation-item">
                <NavLink
                  to="/campaigns"
                  className="sidebar__navigation-link"
                  activeclassname="active"
                >
                  <img
                    src={CampaignIcon}
                    className="sidebar__navigation-icon"
                    alt="Campaigns icon"
                  />
                  {!isCollapsed && (
                    <span className="sidebar__navigation-text">
                      {t("sidebar.campaigns")}
                    </span>
                  )}
                </NavLink>
              </li>
            )}
            {/* <li className="sidebar__navigation-item">
              <NavLink
                to="/upload"
                className="sidebar__navigation-link"
                activeclassname="active"
              >
                <img
                  src={UploadIcon}
                  className="sidebar__navigation-icon"
                  style={{ width: "1.9rem" }}
                  alt="Bot icon"
                />
                {!isCollapsed && (
                  <span className="sidebar__navigation-text">
                    {t("sidebar.upload")}
                  </span>
                )}
              </NavLink>
            </li> */}
          </ul>
        </nav>
      )}

      <section className="sidebar__misc">
        {!isCollaborator && <nav className="misc__actions">
          <ul className="misc__actions-list">
            <li className="misc__actions-item">
              <NavLink
                to="/settings"
                className="misc__actions-link"
                activeclassname="active"
              >
                <img
                  src={SettingsIcon}
                  className="misc__actions-icon"
                  alt="Settings icon"
                />
                {!isCollapsed && (
                  <span className="misc__actions-text">
                    {t("sidebar.settings")}
                  </span>
                )}
              </NavLink>
            </li>
          </ul>
        </nav>}

        {/*         <div className="misc__theme">
          <div className="misc__theme-divider"></div>
          <div className='misc__theme-buttons'>
            <button className='misc__theme-btn misc__theme-btn--light'>
              <img src={LightIcon} className='misc__theme-icon' alt="Light Mode" />
            </button>
            <button className='misc__theme-btn misc__theme-btn--dark'>
              <img src={DarkIcon} className='misc__theme-icon' alt="Dark Mode" />
            </button>
          </div>
        </div> */}

        <nav className="misc__actions">
          <ul className="misc__actions-list">
            <li className="misc__actions-item">
              <button
                onClick={handleSignOut}
                className="misc__actions-link misc__actions-link--logout"
              >
                <img
                  src={LogoutIcon}
                  className="misc__actions-icon"
                  alt="Logout icon"
                />
                {!isCollapsed && (
                  <span className="misc__actions-text">
                    {t("sidebar.logout")}
                  </span>
                )}
              </button>
            </li>
          </ul>
        </nav>
      </section>
    </aside>
  );
};

export default Sidebar;
